/* Detects whether the authenticated browser shell should render the compact mobile/tablet app. */

export const phoneViewportMaxWidth = 768;
export const tabletViewportMaxWidth = 1180;

type DeviceNavigator = {
  userAgent: string;
  platform?: string;
  maxTouchPoints?: number;
};

type DeviceDetectionWindow = {
  innerWidth: number;
  matchMedia?: (query: string) => {matches: boolean};
  navigator: DeviceNavigator;
};

/** Returns whether the viewport reports a touch-first coarse pointer. */
function hasCoarsePointer(viewport: DeviceDetectionWindow): boolean {
  return typeof viewport.matchMedia === 'function' && viewport.matchMedia('(pointer: coarse)').matches;
}

/** Detects iPads that report a desktop Safari user agent but still expose multi-touch Mac hardware. */
function isDesktopUserAgentIpad(navigator: DeviceNavigator): boolean {
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1;
}

/** Detects phone-class user agents so landscape phones keep the tabbed layout. */
function isPhoneUserAgent(navigator: DeviceNavigator): boolean {
  const userAgent = navigator.userAgent.toLowerCase();

  return /iphone|ipod|mobi|opera mini|iemobile|wpdesktop/i.test(userAgent)
    || (userAgent.includes('android') && userAgent.includes('mobile'));
}

/** Determines whether the current browser should use the mobile/tablet shell instead of the desktop windowing shell. */
export function isMobileOrTabletDevice(viewport = globalThis as unknown as DeviceDetectionWindow): boolean {
  const userAgent = viewport.navigator.userAgent.toLowerCase();
  const hasMobileUserAgent = /mobi|android|iphone|ipad|ipod|opera mini|iemobile|wpdesktop|tablet/i.test(userAgent);
  const isPhoneViewport = viewport.innerWidth <= phoneViewportMaxWidth;
  const isTouchTabletViewport = viewport.innerWidth <= tabletViewportMaxWidth && hasCoarsePointer(viewport);

  return hasMobileUserAgent
    || isDesktopUserAgentIpad(viewport.navigator)
    || isPhoneViewport
    || isTouchTabletViewport;
}

/** Determines whether an already-mobile shell should render the wider tablet split view. */
export function shouldUseTabletSplitLayout(viewport = globalThis as unknown as DeviceDetectionWindow): boolean {
  return isMobileOrTabletDevice(viewport)
    && viewport.innerWidth >= phoneViewportMaxWidth
    && !isPhoneUserAgent(viewport.navigator);
}
